import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Form } from '@amzn/awsui-components-react';

import {
    setRequireUpdate,
    updateSelectedInstructor,
} from '../../../store/slices/selectedInstructorSlice';
import {
    InstructorProfileData,
    Provider,
} from '../../../interfaces/instructorProfile';
import BasicInfoFormSection from '../FormSections/BasicInfoFormSection';
import EditBasicInfoSuspensionReasonModal from './EditBasicInfoModals/EditBasicInfoSuspensionReasonModal';
import { handleScrollToError } from '../../../../common/utils/scrollToError';
import useFormValidation, {
    ValidationType,
} from '../../../../common/utils/formValidation';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import handleInstructorDetailNotification from '../../../../common/utils/handleInstructorDetailNotification';
import CancelModal, {
    CancelModalProps,
} from '../../../../common/components/CancelModal/CancelModal';
import { FormSectionMode } from '../../../../common/constants/forms';
import {
    getInstructorProviderValues,
    getIsExternalInstructor,
    InstructorControlArrayFormValues,
    InstructorFormSectionProps,
    InstructorTypeValues,
    ProviderAttributeEditorItem,
    providerValidationConfig,
} from '../Common/Common';
import { INSTRUCTOR_VALIDATION_FIELDS } from '../Common/Validations';
import { SUSPENDED_STATUS } from '../Common/Common';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';

const mode = FormSectionMode.Edit;

const EditBasicInfoForm = ({
    initialFormState,
}: {
    initialFormState: InstructorProfileData;
}) => {
    const match = useRouteMatch<{ id: string }>();
    const history = useHistory();
    const { addNotification } = useNotifications();
    const dispatch = useDispatch();

    const [formValues, setFormValues] = useState(initialFormState);
    const {
        isInvalid,
        isControlArrayInvalid,
        errors,
        controlArrayErrors,
        validateForm,
        validateFormControlArray,
    } = useFormValidation<
        Partial<InstructorProfileData>,
        InstructorControlArrayFormValues
    >();
    const { sponsoringCompany, providers } = getInstructorProviderValues(
        formValues.providers || [],
    );
    const [providerAttributeEditorItems, setProviderAttributeEditorItems] =
        useState(
            providers.map((provider) => ({
                awsClassroom: provider.aws_classrooms_email,
                providerName: provider.provider_name,
                pk: provider.pk,
            })) as Array<ProviderAttributeEditorItem>,
        );
    const [sponsoringCompanyItem, setSponsoringCompanyItem] = useState({
        awsClassroom: sponsoringCompany?.aws_classrooms_email,
        providerName: sponsoringCompany?.provider_name,
        pk: sponsoringCompany?.pk,
    } as ProviderAttributeEditorItem);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const [suspensionReason, setSuspensionReason] =
        useState<OptionDefinition | null>(null);
    const [showSuspensionReason, setShowSuspensionReason] = useState(false);

    const handleFieldEvent = (changes: Partial<InstructorProfileData>) =>
        setFormValues((values: InstructorProfileData) => ({
            ...values,
            ...changes,
        }));

    const handleProviderItemEvent = (
        providerItems: Array<ProviderAttributeEditorItem>,
    ) => {
        setProviderAttributeEditorItems(providerItems);
    };

    const handleSponsoringCompanyItemEvent = (
        sponsoringCompanyItem: Partial<ProviderAttributeEditorItem>,
    ) => {
        setSponsoringCompanyItem((previousValue) => ({
            ...previousValue,
            ...sponsoringCompanyItem,
        }));
    };

    const needsInstructorTypeValidation = useCallback(
        () =>
            mode === FormSectionMode.Edit &&
            getIsExternalInstructor(
                formValues.instructor_type as InstructorTypeValues,
            ) !==
                getIsExternalInstructor(
                    initialFormState.instructor_type as InstructorTypeValues,
                ),
        [initialFormState.instructor_type, formValues.instructor_type],
    );
    const handleAddSuspensionReason = (e: any) => {
        handleFieldEvent({
            suspension_reason: e.label,
        });
        setSuspensionReason(e);
    };

    const handleSubmitEvent = async () => {
        setIsSubmitting(true);
        const invalid = validateForm(
            formValues,
            getValidationConfig(needsInstructorTypeValidation()),
        );
        const isExternal = getIsExternalInstructor(
            formValues.instructor_type as InstructorTypeValues,
        );
        const controlArrayInvalid =
            isExternal && needsInstructorTypeValidation()
                ? validateFormControlArray(
                      {
                          providerAttributeEditorItems,
                          sponsoringCompanyItems: [sponsoringCompanyItem],
                      },
                      providerValidationConfig,
                  )
                : false;

        const isInvalid = invalid || controlArrayInvalid;

        if (isInvalid) {
            setIsSubmitting(false);
            handleScrollToError();
        } else {
            const instructorData: InstructorProfileData = {
                ...formValues,
                ...(isExternal
                    ? {
                          providers: [
                              ...providerAttributeEditorItems.map(
                                  (providerItem) =>
                                      ({
                                          pk: providerItem.pk,
                                          aws_classrooms_email:
                                              providerItem.awsClassroom,
                                          provider_name:
                                              providerItem.providerName,
                                          is_sponsor_company: false,
                                      }) as Provider,
                              ),
                              {
                                  aws_classrooms_email:
                                      sponsoringCompanyItem.awsClassroom,
                                  provider_name:
                                      sponsoringCompanyItem.providerName,
                                  is_sponsor_company: true,
                                  pk: sponsoringCompanyItem.pk,
                              } as Provider,
                          ],
                      }
                    : {}),
            };
            const isSuccessful = await handleInstructorDetailNotification(
                dispatch<any>(
                    updateSelectedInstructor(match.params.id, instructorData),
                ),
                addNotification,
                'basic-info',
            );
            if (!isSuccessful) {
                setIsSubmitting(false);
            } else {
                dispatch(setRequireUpdate(true));

                // if successful, go back to the details page
                navigateToDetailPage();
            }
        }
    };

    const navigateToDetailPage = () => {
        history.push({
            pathname: `/instructors/${match.params.id}`,
        });
    };

    const cancelModalProps: CancelModalProps = {
        isCancelModalVisible,
        setIsCancelModalVisible,
        isSubmitting,
        onCancelConfirm: navigateToDetailPage,
        testPrefix: 'EditInstructorBasicInfo',
    };

    const basicInfoProps: InstructorFormSectionProps<InstructorProfileData> = {
        formValues,
        errors,
        handleFieldEvent,
        mode,
        handleProviderItemEvent,
        handleSponsoringCompanyItemEvent,
        providerAttributeEditorItems,
        sponsoringCompanyItem,
        controlArrayErrors,
    };

    const getValidationConfig = useCallback(
        (
            needsInstructorTypeValidation: boolean,
        ): {
            [key in ValidationType]?: Array<keyof InstructorProfileData>;
        } => {
            // Basic validation
            const validationConfig = {
                required: INSTRUCTOR_VALIDATION_FIELDS.BASIC_REQUIRED,
                email: INSTRUCTOR_VALIDATION_FIELDS.BASIC_EMAIL,
            };

            if (needsInstructorTypeValidation) {
                if (
                    getIsExternalInstructor(
                        formValues.instructor_type as InstructorTypeValues,
                    )
                ) {
                    let required = [
                        ...INSTRUCTOR_VALIDATION_FIELDS.EXTERNAL_REQUIRED,
                        ...(formValues.is_freelancer
                            ? INSTRUCTOR_VALIDATION_FIELDS.EXTERNAL_FREELANCER_REQUIRED
                            : []),
                    ];

                    let email = [
                        ...validationConfig.email,
                        ...INSTRUCTOR_VALIDATION_FIELDS.EXTERNAL_EMAIL,
                        ...(formValues.is_freelancer
                            ? INSTRUCTOR_VALIDATION_FIELDS.EXTERNAL_FREELANCER_EMAIL
                            : []),
                    ];

                    return {
                        ...validationConfig,
                        required: [...validationConfig.required, ...required],
                        email: email,
                    };
                } else {
                    return {
                        required: [
                            ...validationConfig.required,
                            ...INSTRUCTOR_VALIDATION_FIELDS.INTERNAL_REQUIRED,
                        ],
                        email: [
                            ...validationConfig.email,
                            ...INSTRUCTOR_VALIDATION_FIELDS.INTERNAL_EMAIL,
                        ],
                    };
                }
            }
            return validationConfig;
        },
        [formValues.is_freelancer, formValues.instructor_type],
    );

    useEffect(() => {
        if (isInvalid) {
            validateForm(
                formValues,
                getValidationConfig(needsInstructorTypeValidation()),
            );
        }

        if (isControlArrayInvalid) {
            validateFormControlArray(
                {
                    providerAttributeEditorItems,
                    sponsoringCompanyItems: [sponsoringCompanyItem],
                },
                providerValidationConfig,
            );
        }
    }, [
        dispatch,
        formValues,
        getValidationConfig,
        isInvalid,
        validateForm,
        needsInstructorTypeValidation,
        isControlArrayInvalid,
        validateFormControlArray,
        providerAttributeEditorItems,
        sponsoringCompanyItem,
    ]);

    const getSuspensionReasonModal = async () => {
        if (
            formValues.instructor_status === SUSPENDED_STATUS &&
            initialFormState.instructor_status !== SUSPENDED_STATUS
        ) {
            setShowSuspensionReason(true);
        } else {
            if (
                initialFormState.instructor_status === SUSPENDED_STATUS &&
                formValues.instructor_status !== SUSPENDED_STATUS
            ) {
                formValues.suspension_reason = null;
            }
            handleSubmitEvent();
        }
    };

    const cancelSuspensionReasonModal = () => {
        setShowSuspensionReason(false);
    };

    return (
        <>
            <Form
                data-testid="InstructorEditBasicInfoForm"
                actions={
                    <div className="awsui-util-pt-xl">
                        <Button
                            variant="link"
                            data-testid={`InstructorEditBasicInfo-CancelBtn`}
                            disabled={isSubmitting}
                            onClick={() => {
                                setIsCancelModalVisible(true);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            data-testid={`InstructorEditBasicInfo-SubmitBtn`}
                            formAction="submit"
                            disabled={isSubmitting}
                            onClick={getSuspensionReasonModal}
                        >
                            {isSubmitting ? 'Saving' : 'Save'}
                        </Button>
                    </div>
                }
            >
                <BasicInfoFormSection {...basicInfoProps} />
            </Form>
            <CancelModal {...cancelModalProps} />
            <EditBasicInfoSuspensionReasonModal
                visible={showSuspensionReason}
                data-testid="EditBasicInfoSuspensionReasonModal"
                onCancel={cancelSuspensionReasonModal}
                onConfirm={handleSubmitEvent}
                setSuspensionReason={handleAddSuspensionReason}
                suspensionReason={suspensionReason}
            />
        </>
    );
};

export default EditBasicInfoForm;
